

















































































































































































































































































































import Component, { mixins } from 'vue-class-component';
import { loadWidget } from '@/utils/helpers';
import { namespace } from 'vuex-class';
import { IPaginate } from '@/types/types';
import { IBulkBatch, IBulkChunk, IQueueJob, IQueueStats } from '@/store/modules/messages-management';
import timestamp from '@/filters/timestamp';
import { Watch } from 'vue-property-decorator';
import WidgetMixins from '@/mixins/WidgetMixins';
import moment from 'moment';
import qs from 'qs';

const messagesManagementModule = namespace('messagesManagement');

@Component({
  name: 'MessagesManagement',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
  },
  filters: {
    timestamp,
  },
})
export default class MessagesManagement extends mixins(WidgetMixins) {
  @messagesManagementModule.Getter('getQueueStats') stats!: IQueueStats | null;
  @messagesManagementModule.Getter('getJobs') jobs!: IQueueJob[];
  @messagesManagementModule.Getter('getJobsPagination') jobsPagination!: IPaginate;
  @messagesManagementModule.Getter('getAllBatches') batches!: IBulkBatch[];
  @messagesManagementModule.Getter('getPagination') batchesPagination!: IPaginate;
  @messagesManagementModule.Getter('getChunks') chunks!: IBulkChunk[];
  @messagesManagementModule.Getter('getChunksPagination') chunksPagination!: IPaginate;

  activeTab: number = 0;

  jobStates: string[] = ['failed', 'waiting', 'active', 'delayed', 'completed', 'paused'];
  jobState: string = 'failed';
  jobOptions: any = {};
  jobsQuery: string = '';

  batchStatuses: string[] = ['ALL', 'PENDING', 'QUEUED', 'PROCESSING', 'COMPLETED', 'PARTIAL', 'FAILED'];
  batchStatus: string = 'ALL';
  batchSearchTerm: string = '';
  batchOptions: any = {};
  batchesQuery: string = '';

  chunksDialog: boolean = false;
  chunkOptions: any = {};
  chunksQuery: string = '';
  activeBatch: IBulkBatch | null = null;

  confirmDialog: boolean = false;
  confirmTitle: string = '';
  confirmBody: string = '';
  confirmColor: string = 'primary';
  pendingAction: (() => void) | null = null;
  refreshTimer: number | null = null;

  jobHeaders: Array<{ text: string; value: string; sortable?: boolean }> = [
    { text: 'Job ID', value: 'id', sortable: false },
    { text: 'Type', value: 'name', sortable: false },
    { text: 'Age', value: 'ageMs', sortable: false },
    { text: 'Messages', value: 'messageCount', sortable: false },
    { text: 'Transaction', value: 'transactionId', sortable: false },
    { text: 'Failure Reason', value: 'failedReason', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  batchHeaders: Array<{ text: string; value: string; sortable?: boolean }> = [
    { text: 'Date', value: 'createdAt', sortable: false },
    { text: 'Transaction', value: 'transactionId', sortable: false },
    { text: 'Sender', value: 'senderAddress', sortable: false },
    { text: 'Customer', value: 'userName', sortable: false },
    { text: 'Messages', value: 'totalMessages', sortable: false },
    { text: 'Chunks', value: 'chunks', sortable: false },
    { text: 'Refunded', value: 'refundedSegments', sortable: false },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  chunkHeaders: Array<{ text: string; value: string; sortable?: boolean }> = [
    { text: '#', value: 'chunkIndex', sortable: false },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Messages', value: 'messageCount', sortable: false },
    { text: 'Segments', value: 'segments', sortable: false },
    { text: 'Last Error', value: 'lastError', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  get isDialogLoading(): boolean {
    return this.$store.getters['getIsDialogLoading'];
  }

  get statCards(): Array<{ label: string; value: string | number; icon: string; variant: string; sub: string }> {
    if (!this.stats) return [];
    const counts = this.stats.counts || {};
    const waiting = counts.waiting ?? 0;
    const active = counts.active ?? 0;
    const delayed = counts.delayed ?? 0;
    const failed = counts.failed ?? 0;
    const waitingAgeMs = this.stats.oldestWaitingAgeMs;
    const activeAgeMs = this.stats.oldestActiveAgeMs;
    return [
      {
        label: 'Waiting',
        value: waiting,
        icon: 'hourglass_empty',
        variant: waiting > 100 ? 'error' : 'info',
        sub: waiting > 100 ? 'Backlog building up' : 'Jobs queued for dispatch',
      },
      {
        label: 'Active',
        value: active,
        icon: 'bolt',
        variant: 'primary',
        sub: 'Processing right now',
      },
      {
        label: 'Delayed',
        value: delayed,
        icon: 'schedule',
        variant: 'ink',
        sub: 'Scheduled for later',
      },
      {
        label: 'Failed',
        value: failed,
        icon: failed > 0 ? 'error_outline' : 'task_alt',
        variant: failed > 0 ? 'error' : 'success',
        sub: failed > 0 ? 'Need attention — see Queue Jobs' : 'No failures recorded',
      },
      {
        label: 'Oldest Waiting',
        value: this.humanizeMs(waitingAgeMs),
        icon: 'timer',
        variant: waitingAgeMs > 10 * 60 * 1000 ? 'error' : waitingAgeMs > 0 ? 'warning' : 'ink',
        sub: waitingAgeMs > 10 * 60 * 1000 ? 'Jobs are not being picked up' : 'Queue pickup latency',
      },
      {
        label: 'Oldest Active',
        value: this.humanizeMs(activeAgeMs),
        icon: 'pending_actions',
        variant: activeAgeMs > 30 * 60 * 1000 ? 'error' : activeAgeMs > 0 ? 'warning' : 'ink',
        sub: activeAgeMs > 30 * 60 * 1000 ? 'Possible stuck job' : 'Longest running job',
      },
    ];
  }

  get sparklineValues(): number[] {
    return ((this.stats && this.stats.history) || []).map(sample => sample.waiting);
  }

  get sparklineSummary(): string {
    const history = (this.stats && this.stats.history) || [];
    if (history.length === 0) return 'No samples yet';
    const values = this.sparklineValues;
    const current = values[values.length - 1];
    const peak = Math.max(...values);
    const since = moment(history[0].ts).format('DD/MM HH:mm');
    return `now ${current} · peak ${peak} · since ${since}`;
  }

  humanizeMs(ms: number): string {
    if (!ms || ms <= 0) return '—';
    return moment.duration(ms).humanize();
  }

  truncateText(value: string | null, length: number): string {
    if (!value) return '—';
    return value.length > length ? `${value.slice(0, length)}…` : value;
  }

  statusClass(status: string): string {
    const normalized = (status || '').toLowerCase();
    if (['completed', 'sent', 'delivered'].includes(normalized)) return 'ka-status--success';
    if (['failed'].includes(normalized)) return 'ka-status--error';
    if (['pending', 'queued'].includes(normalized)) return 'ka-status--info';
    return 'ka-status--warning';
  }

  buildJobsQuery(overrides: any = {}): string {
    return qs.stringify(
      {
        state: this.jobState,
        page: this.jobsPagination.page,
        size: this.jobsPagination.itemsPerPage,
        ...overrides,
      },
      { skipNulls: true, addQueryPrefix: true },
    );
  }

  buildBatchesQuery(overrides: any = {}): string {
    return qs.stringify(
      {
        page: this.batchesPagination.page,
        size: this.batchesPagination.itemsPerPage,
        ...(this.batchStatus !== 'ALL' && { status: this.batchStatus }),
        ...(this.batchSearchTerm && { searchTerm: this.batchSearchTerm }),
        ...overrides,
      },
      { skipNulls: true, addQueryPrefix: true },
    );
  }

  fetchJobs(): void {
    this.jobsQuery = this.buildJobsQuery();
    this.$store.dispatch('messagesManagement/listJobs', this.jobsQuery);
  }

  fetchBatches(): void {
    this.batchesQuery = this.buildBatchesQuery();
    this.$store.dispatch('messagesManagement/listBatches', this.batchesQuery);
  }

  refreshAll(): void {
    this.$store.dispatch('messagesManagement/queueStats');
    if (this.activeTab === 0) this.fetchJobs();
    else this.fetchBatches();
  }

  clearBatchSearch(): void {
    this.batchSearchTerm = '';
    this.fetchBatches();
  }

  openJobAction(job: IQueueJob, action: 'retry' | 'remove'): void {
    this.confirmTitle = action === 'retry' ? 'Retry Job' : 'Remove Job';
    this.confirmColor = action === 'retry' ? 'orange' : 'red';
    this.confirmBody =
      action === 'retry'
        ? `Requeue job <strong>${job.id}</strong> (${job.messageCount ??
            '?'} messages) for a fresh dispatch? Credits will be re-reserved at dispatch time.`
        : `Permanently remove job <strong>${job.id}</strong> from the queue? The messages in it will <strong>not</strong> be sent.`;
    this.pendingAction = () => {
      this.$store.dispatch(`messagesManagement/${action === 'retry' ? 'retryJob' : 'removeJob'}`, {
        jobId: job.id,
        query: this.jobsQuery,
      });
    };
    this.confirmDialog = true;
  }

  openChunks(batch: IBulkBatch): void {
    this.activeBatch = batch;
    this.chunksDialog = true;
    this.fetchChunks(1, 15);
  }

  fetchChunks(page: number, size: number): void {
    if (!this.activeBatch) return;
    this.chunksQuery = qs.stringify({ page, size }, { addQueryPrefix: true });
    this.$store.dispatch('messagesManagement/listChunks', {
      batchId: this.activeBatch.id,
      query: this.chunksQuery,
    });
  }

  openResolve(chunk: IBulkChunk, action: 'refund' | 'mark-sent'): void {
    this.confirmTitle = action === 'refund' ? 'Fail Chunk & Refund Credits' : 'Confirm Chunk as Sent';
    this.confirmColor = action === 'refund' ? 'red' : 'success';
    this.confirmBody =
      action === 'refund'
        ? `You confirmed against the SMPP logs that chunk <strong>#${chunk.chunkIndex}</strong> (${chunk.messageCount} messages, ${chunk.segments} segments) was <strong>never submitted</strong>? The chunk will be failed and ${chunk.segments} segments refunded to the customer. Refunds are rejected for chunks that were already refunded when they failed.`
        : `You confirmed against the SMPP logs that chunk <strong>#${chunk.chunkIndex}</strong> (${chunk.messageCount} messages) <strong>was submitted</strong>? The chunk and its recipients will be marked SENT with no refund.`;
    this.pendingAction = () => {
      if (!this.activeBatch) return;
      this.$store.dispatch('messagesManagement/resolveChunk', {
        chunkId: chunk.id,
        action,
        batchId: this.activeBatch.id,
        chunksQuery: this.chunksQuery,
        batchesQuery: this.batchesQuery,
      });
    };
    this.confirmDialog = true;
  }

  confirmAction(): void {
    if (this.pendingAction) this.pendingAction();
    this.pendingAction = null;
    this.confirmDialog = false;
  }

  @Watch('jobState')
  onJobStateChange(): void {
    this.fetchJobs();
  }

  @Watch('batchStatus')
  onBatchStatusChange(): void {
    this.fetchBatches();
  }

  @Watch('activeTab')
  onTabChange(tab: number): void {
    if (tab === 0) this.fetchJobs();
    else this.fetchBatches();
  }

  @Watch('jobOptions', { deep: true })
  onJobOptionsChange(paginate: IPaginate): void {
    const query = this.buildJobsQuery({ page: paginate.page, size: paginate.itemsPerPage });
    if (query === this.jobsQuery) return;
    this.jobsQuery = query;
    this.$store.dispatch('messagesManagement/listJobs', this.jobsQuery);
  }

  @Watch('batchOptions', { deep: true })
  onBatchOptionsChange(paginate: IPaginate): void {
    const query = this.buildBatchesQuery({ page: paginate.page, size: paginate.itemsPerPage });
    if (query === this.batchesQuery) return;
    this.batchesQuery = query;
    this.$store.dispatch('messagesManagement/listBatches', this.batchesQuery);
  }

  @Watch('chunkOptions', { deep: true })
  onChunkOptionsChange(paginate: IPaginate): void {
    if (!this.chunksDialog) return;
    this.fetchChunks(paginate.page, paginate.itemsPerPage);
  }

  created(): void {
    this.$store.dispatch('auth/me');
    this.$store.dispatch('messagesManagement/queueStats');
    this.fetchJobs();
    // Silent stats refresh while the page is open; skipped when the tab is hidden.
    this.refreshTimer = window.setInterval(() => {
      if (!document.hidden) {
        this.$store.dispatch('messagesManagement/queueStats');
      }
    }, 30000);
  }

  beforeDestroy(): void {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
